import * as React from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Button, cn } from "@finster/ui";
import { ChevronRight, FolderSymlink, Plus } from "lucide-react";
import { UserFileSelector } from "./UserFileSelector";

export type ComposerAttachmentPickerVariant = "menu" | "direct";

export interface ComposerAttachmentSecondaryPanelOpenChangeOptions {
  /** Defaults to true when an open panel closes. */
  restoreFocus?: boolean;
  /** Reopens the attachment menu after a secondary panel Back action. */
  reopenMenu?: boolean;
}

export interface ComposerAttachmentSecondaryPanelRenderProps {
  open: boolean;
  anchorRef: React.RefObject<HTMLDivElement | null>;
  triggerRef: React.RefObject<HTMLButtonElement | null>;
  onOpenChange: (
    open: boolean,
    options?: ComposerAttachmentSecondaryPanelOpenChangeOptions
  ) => void;
  restoreTriggerFocus: () => void;
}

export interface ComposerAttachmentSecondaryPanel {
  /** Controlled panel visibility owned by the caller. */
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Renders a host-neutral secondary surface anchored to the attachment
   * trigger. The picker owns focus restoration when the controlled panel
   * transitions from open to closed.
   */
  render: (props: ComposerAttachmentSecondaryPanelRenderProps) => React.ReactNode;
}

export interface ComposerAttachmentMenuAction {
  id?: string;
  label: string;
  /** Muted secondary text rendered inline after the label. */
  description?: string;
  icon?: React.ReactNode;
  onSelect?: () => void | Promise<void>;
  disabled?: boolean;
  /** Shows the disclosure affordance used by actions that open another panel. */
  trailingChevron?: boolean;
  /**
   * Controls which visual group contains the action. `"top"` and `"middle"`
   * render above Open Your Files and are each closed by a separator. Defaults
   * to `"bottom"`, which groups the action with Open Your Files.
   */
  placement?: "top" | "middle" | "bottom";
  /**
   * Marks the action as a toggle and reflects its current state. Supplying this
   * switches the row to `menuitemcheckbox` semantics.
   */
  active?: boolean;
  /**
   * The action moves focus itself, so the picker must not pull it back to the
   * trigger once the menu closes.
   */
  managesFocus?: boolean;
  /** Optional controlled secondary surface associated with this menu action. */
  secondaryPanel?: ComposerAttachmentSecondaryPanel;
}

/**
 * Matches every focusable menu row: plain `menuitem` plus the
 * `menuitemcheckbox` role that toggle actions opt into. Kept as one prefix
 * selector rather than a comma-separated list because jsdom returns list
 * matches grouped by selector instead of in document order, which would make
 * arrow-key order diverge from the browser.
 */
const MENU_ITEM_SELECTOR = '[role^="menuitem"]:not(:disabled)';

export interface ComposerAttachmentPickerProps {
  variant?: ComposerAttachmentPickerVariant;
  selectedFileIds: readonly string[];
  onSelectedFileIdsChange: (fileIds: string[]) => void;
  disabled?: boolean;
  /** Additional host-agnostic actions appended after Open Your Files. */
  menuActions?: readonly ComposerAttachmentMenuAction[];
}

/**
 * Shared composer entry point for attachments.
 *
 * `direct` keeps the existing folder-button selector. `menu` presents a
 * compact attachment menu and opens that same selector implementation from
 * its built-in Open Your Files action.
 */
export const ComposerAttachmentPicker: React.FC<ComposerAttachmentPickerProps> = ({
  variant = "direct",
  selectedFileIds,
  onSelectedFileIdsChange,
  disabled = false,
  menuActions = [],
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [fileSelectorOpen, setFileSelectorOpen] = useState(false);
  const menuRootRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const triggerButtonRef = useRef<HTMLButtonElement>(null);
  const suppressSecondaryFocusRestoreRef = useRef(false);
  const secondaryPanelOpen = menuActions.some((action) => action.secondaryPanel?.open);
  const wasSecondaryPanelOpenRef = useRef(secondaryPanelOpen);

  const restoreTriggerFocus = useCallback(() => {
    window.setTimeout(() => {
      triggerButtonRef.current?.focus();
    }, 0);
  }, []);

  const closeMenu = useCallback(
    (restoreFocus = false) => {
      setMenuOpen(false);
      if (restoreFocus) {
        restoreTriggerFocus();
      }
    },
    [restoreTriggerFocus]
  );

  const handleMenuKeyDown = useCallback((event: React.KeyboardEvent<HTMLDivElement>) => {
    const enabledItems = Array.from(
      menuRef.current?.querySelectorAll<HTMLButtonElement>(MENU_ITEM_SELECTOR) ?? []
    );
    if (enabledItems.length === 0) return;

    const currentIndex = enabledItems.indexOf(document.activeElement as HTMLButtonElement);
    let nextItem: HTMLButtonElement | undefined;
    switch (event.key) {
      case "ArrowDown":
        nextItem = enabledItems[(currentIndex + 1) % enabledItems.length];
        break;
      case "ArrowUp":
        nextItem = enabledItems[currentIndex <= 0 ? enabledItems.length - 1 : currentIndex - 1];
        break;
      case "Home":
        nextItem = enabledItems[0];
        break;
      case "End":
        nextItem = enabledItems[enabledItems.length - 1];
        break;
      default:
        return;
    }

    event.preventDefault();
    nextItem?.focus();
  }, []);

  const handleMenuActionSelect = useCallback(
    (action: ComposerAttachmentMenuAction) => {
      setMenuOpen(false);
      action.secondaryPanel?.onOpenChange(true);
      const shouldRestoreFocus = !action.secondaryPanel && !action.managesFocus;
      try {
        const result = action.onSelect?.();
        if (result) {
          void result
            .catch(() => undefined)
            .finally(() => {
              if (shouldRestoreFocus) restoreTriggerFocus();
            });
          return;
        }
      } catch {
        // The action owns its error state; the picker still restores focus.
      }
      if (shouldRestoreFocus) restoreTriggerFocus();
    },
    [restoreTriggerFocus]
  );

  const handleMenuBlur = useCallback(
    (event: React.FocusEvent<HTMLDivElement>) => {
      // Pointer dismissal is owned by the trigger toggle and the outside-click
      // listener. Blur only handles keyboard focus leaving the entire picker.
      const nextFocus = event.relatedTarget as Node | null;
      if (!nextFocus || menuRootRef.current?.contains(nextFocus)) return;
      closeMenu();
    },
    [closeMenu]
  );

  useEffect(() => {
    if (!menuOpen) return undefined;

    menuRef.current?.querySelector<HTMLButtonElement>(MENU_ITEM_SELECTOR)?.focus();

    const handlePointerDown = (event: MouseEvent) => {
      if (!menuRootRef.current?.contains(event.target as Node)) {
        closeMenu();
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        closeMenu(true);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [closeMenu, menuOpen]);

  useEffect(() => {
    if (wasSecondaryPanelOpenRef.current && !secondaryPanelOpen) {
      if (!suppressSecondaryFocusRestoreRef.current) {
        restoreTriggerFocus();
      }
      suppressSecondaryFocusRestoreRef.current = false;
    }
    wasSecondaryPanelOpenRef.current = secondaryPanelOpen;
  }, [restoreTriggerFocus, secondaryPanelOpen]);

  useEffect(() => {
    if (!disabled) return;
    setMenuOpen(false);
    setFileSelectorOpen(false);
    menuActions.forEach((action) => {
      if (action.secondaryPanel?.open) {
        action.secondaryPanel.onOpenChange(false);
      }
    });
  }, [disabled, menuActions]);

  const handleFileSelectorOpenChange = useCallback((open: boolean) => {
    setFileSelectorOpen(open);
    if (open) {
      setMenuOpen(false);
    }
  }, []);

  if (variant === "direct") {
    return (
      <UserFileSelector
        selectedFileIds={selectedFileIds}
        onSelectedFileIdsChange={onSelectedFileIdsChange}
        disabled={disabled}
      />
    );
  }

  const topActions = menuActions.filter((action) => action.placement === "top");
  const middleActions = menuActions.filter((action) => action.placement === "middle");
  const bottomActions = menuActions.filter(
    (action) => action.placement !== "top" && action.placement !== "middle"
  );

  const renderMenuAction = (
    action: ComposerAttachmentMenuAction,
    keyPrefix: string,
    index: number
  ) => {
    const isToggle = action.active !== undefined;
    return (
      <button
        key={action.id ?? `${keyPrefix}-${action.label}-${index}`}
        type="button"
        role={isToggle ? "menuitemcheckbox" : "menuitem"}
        aria-checked={isToggle ? action.active : undefined}
        // The label and description are separate inline spans, so the computed
        // name would run them together ("PlanRefine instructions").
        aria-label={action.description ? `${action.label}. ${action.description}` : undefined}
        tabIndex={-1}
        disabled={action.disabled}
        aria-haspopup={action.secondaryPanel ? "dialog" : undefined}
        aria-expanded={action.secondaryPanel ? action.secondaryPanel.open : undefined}
        onClick={() => handleMenuActionSelect(action)}
        className={cn(
          "flex h-8 w-full cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border-0 bg-transparent px-2 text-left text-xs font-medium text-foreground hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring-focus disabled:cursor-not-allowed disabled:opacity-50",
          action.active && "bg-primary-muted text-primary hover:bg-primary-muted"
        )}
      >
        <span className="flex size-4 shrink-0 items-center justify-center [&>svg]:size-4">
          {action.icon}
        </span>
        <span className="flex min-w-0 flex-1 items-center gap-1">
          <span className="shrink-0">{action.label}</span>
          {action.description && (
            <span className="min-w-0 truncate font-normal text-muted-foreground">
              {action.description}
            </span>
          )}
        </span>
        {(action.trailingChevron || action.secondaryPanel) && (
          <ChevronRight className="ml-auto size-4 shrink-0 text-muted-foreground" aria-hidden />
        )}
      </button>
    );
  };

  return (
    <UserFileSelector
      selectedFileIds={selectedFileIds}
      onSelectedFileIdsChange={onSelectedFileIdsChange}
      disabled={disabled}
      open={fileSelectorOpen}
      onOpenChange={handleFileSelectorOpenChange}
      renderTrigger={({ triggerRef }) => (
        <div ref={menuRootRef} className="relative">
          <Button
            ref={(node) => {
              triggerButtonRef.current = node;
              if (typeof triggerRef === "function") {
                triggerRef(node);
              } else if (triggerRef) {
                (triggerRef as React.MutableRefObject<HTMLButtonElement | null>).current = node;
              }
            }}
            type="button"
            variant="outline"
            size="icon"
            disabled={disabled}
            aria-label="Add attachment"
            aria-haspopup="menu"
            aria-expanded={menuOpen || fileSelectorOpen || secondaryPanelOpen}
            onClick={() => {
              if (secondaryPanelOpen) {
                menuActions.forEach((action) => {
                  if (action.secondaryPanel?.open) {
                    action.secondaryPanel.onOpenChange(false);
                  }
                });
                return;
              }
              if (fileSelectorOpen) {
                setFileSelectorOpen(false);
                return;
              }
              setMenuOpen(!menuOpen);
            }}
            // Sits beside DataSourceSelector in the composer's control row, so
            // the height and hover treatment track that chip at default
            // density; a shorter or borderless trigger reads as misaligned.
            className={cn(
              "h-9 w-9 shrink-0 cursor-pointer rounded-md bg-background hover:border-primary hover:bg-primary/5 hover:text-foreground",
              (menuOpen || fileSelectorOpen || secondaryPanelOpen || selectedFileIds.length > 0) &&
                "border-primary bg-accent text-primary"
            )}
          >
            <Plus className="h-4 w-4" aria-hidden />
          </Button>

          {menuOpen && (
            <div
              ref={menuRef}
              role="menu"
              aria-label="Attachment options"
              onKeyDown={handleMenuKeyDown}
              onBlur={handleMenuBlur}
              className="absolute bottom-full left-0 z-50 mb-2 min-w-[168px] max-w-[min(20rem,calc(100vw-1.5rem))] rounded-lg border border-border bg-background p-1 text-foreground shadow-lg"
            >
              {topActions.map((action, index) => renderMenuAction(action, "top", index))}
              {topActions.length > 0 && <div role="separator" className="my-1 h-px bg-border" />}
              {middleActions.map((action, index) =>
                renderMenuAction(action, "middle", index)
              )}
              {middleActions.length > 0 && (
                <div role="separator" className="my-1 h-px bg-border" />
              )}
              <button
                type="button"
                role="menuitem"
                tabIndex={-1}
                onClick={() => {
                  setMenuOpen(false);
                  setFileSelectorOpen(true);
                }}
                className="flex h-8 w-full cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border-0 bg-transparent px-2 text-left text-xs font-medium text-foreground hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring-focus"
              >
                <span className="flex size-4 shrink-0 items-center justify-center">
                  <FolderSymlink className="h-4 w-4" aria-hidden />
                </span>
                <span className="flex-1">Open Your Files</span>
                <ChevronRight className="ml-auto size-4 shrink-0 text-muted-foreground" aria-hidden />
              </button>
              {bottomActions.map((action, index) => renderMenuAction(action, "bottom", index))}
            </div>
          )}
          {menuActions.map((action, index) => {
            const secondaryPanel = action.secondaryPanel;
            if (!secondaryPanel?.open) return null;
            return (
              <React.Fragment key={action.id ?? `${action.label}-${index}`}>
                {secondaryPanel.render({
                  open: secondaryPanel.open,
                  anchorRef: menuRootRef,
                  triggerRef: triggerButtonRef,
                  onOpenChange: (nextOpen, options) => {
                    if (!nextOpen && options?.restoreFocus === false) {
                      suppressSecondaryFocusRestoreRef.current = true;
                    }
                    secondaryPanel.onOpenChange(nextOpen);
                    if (!nextOpen && options?.reopenMenu) {
                      setMenuOpen(true);
                    }
                  },
                  restoreTriggerFocus,
                })}
              </React.Fragment>
            );
          })}
        </div>
      )}
    />
  );
};
